import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toBase64 } from './useStoredFiles'; // item 865 — shared file→base64 encoder

// item 843 — the EXTERNAL Customer Builder portal API (api/customer-portal). Everything here is
// hard-scoped + fail-closed on the backend to the caller's ONE bound customer; no pricing is ever
// returned. These hooks are used ONLY by the portal shell (src/portal), never the internal app.
const apiUrl = (): string => process.env.REACT_APP_BASIX_API || '';

export interface PortalProposalRow {
  id: string;
  proposalNumber: string;
  title: string;
  createdDate: string;
  status: string; // Draft | In review | Sent
  editable: boolean;
  canViewPricedPdf: boolean;
}

export interface PortalProposalDetail extends PortalProposalRow {
  statementOfWork?: string;
  serviceAddress?: { street?: string; unit?: string; city?: string; state?: string; zip?: string; country?: string } | null;
  servicePhone?: string;
  serviceEmail?: string;
  rnwyVenuesJson?: string | null;
  rnwySaleType: number;
  rnwyHelpDeskTier: number;
  showPerpetualOnPdf: boolean;
  showTwoYearOnPdf: boolean;
  showFiveYearOnPdf: boolean;
  showHelpDeskOnPdf: boolean;
  showSwitchPilotOnPdf: boolean;
}

export interface PortalProposalInput {
  proposalTitle: string;
  // item 871 — the request's home: an existing project of theirs, a NEW project they name
  // (created for real; Pulse can rename/archive), or neither (the drafts bucket).
  projectId?: string | null;
  newProjectName?: string | null;
  serviceAddress?: PortalProposalDetail['serviceAddress'];
  servicePhone?: string;
  serviceEmail?: string;
  statementOfWork?: string;
  rnwyVenuesJson?: string | null;
  rnwySaleType: number;
  rnwyHelpDeskTier: number;
  showPerpetualOnPdf: boolean;
  showTwoYearOnPdf: boolean;
  showFiveYearOnPdf: boolean;
  showHelpDeskOnPdf: boolean;
  showSwitchPilotOnPdf: boolean;
}

// item 893 — CONSULTANT account switch: when an assigned account is selected, EVERY
// customer-portal request carries `?account=` (the server validates the assignment and
// re-scopes the PROPOSAL side; billing always stays the bound customer). Registered once
// at module load; the key only ever exists for External portal sessions.
const PORTAL_ACCOUNT_KEY = 'portal-account';
export const getPortalAccount = (): string | null => localStorage.getItem(PORTAL_ACCOUNT_KEY);
export const setPortalAccount = (id: string | null): void => {
  if (id) localStorage.setItem(PORTAL_ACCOUNT_KEY, id);
  else localStorage.removeItem(PORTAL_ACCOUNT_KEY);
};
axios.interceptors.request.use((config) => {
  const account = getPortalAccount();
  if (account && (config.url ?? '').includes('customer-portal')) {
    // eslint-disable-next-line no-param-reassign
    config.params = { ...(config.params ?? {}), account };
  }
  return config;
});

export interface PortalAccount { id: string; name: string }

// item 890 — which portal halves this login gets (per-user, admin-set on the user form):
// the pricing portal (proposal builder), invoices & billing, or both. The hub renders around it.
// item 893 — plus the accounts they can request proposals for (bound customer first).
export const usePortalCapabilities = () => useQuery('portal-capabilities', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/capabilities`);
  return data as {
    proposals: boolean;
    invoices: boolean;
    contracts?: boolean;
    // item 1388 — seat bound to an END USER contact: subscriptions, invoices, agreements only
    // (proposals arrives false; the server fence 403s the builder/deals/fleet paths regardless)
    endUser?: boolean;
    boundCustomerId: string;
    accounts: PortalAccount[];
  };
}, { staleTime: 60_000 });

export const useMySubmissions = () => useQuery('portal-proposals', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/proposals`);
  return data as PortalProposalRow[];
});

export const usePortalProposal = (id?: string) => useQuery(
  ['portal-proposal', id],
  async () => {
    const { data } = await axios.get(`${apiUrl()}customer-portal/proposals/${id}`);
    return data as PortalProposalDetail;
  },
  { enabled: !!id },
);

export const usePortalActions = () => {
  const qc = useQueryClient();
  const refresh = () => {
    qc.invalidateQueries('portal-proposals');
    qc.invalidateQueries('portal-proposal');
  };
  const create = async (input: PortalProposalInput): Promise<{ id: string; proposalNumber: string }> => {
    const { data } = await axios.post(`${apiUrl()}customer-portal/proposals`, input);
    return data;
  };
  const update = async (id: string, input: PortalProposalInput): Promise<{ id: string }> => {
    const { data } = await axios.put(`${apiUrl()}customer-portal/proposals/${id}`, input);
    return data;
  };
  const submit = async (id: string): Promise<{ id: string; status: string }> => {
    const { data } = await axios.post(`${apiUrl()}customer-portal/proposals/${id}/submit`);
    return data;
  };
  const duplicate = async (id: string): Promise<{ id: string; proposalNumber: string }> => {
    const { data } = await axios.post(`${apiUrl()}customer-portal/proposals/${id}/duplicate`);
    return data;
  };
  // item 1028 — retire a DRAFT (server enforces drafts-only; submitted requests are withdrawn
  // by Pulse internally)
  const archive = async (id: string): Promise<void> => {
    await axios.post(`${apiUrl()}customer-portal/proposals/${id}/archive`);
  };
  return {
    create, update, submit, duplicate, archive, refresh,
  };
};

// Fetch the priced PDF via axios (so the auth header is attached) and open it in a new tab.
// item 867 (Chad: "I can't download it") — the object URL is NEVER revoked: Chrome's PDF-viewer
// download button re-fetches the blob URL, and the old 60s revoke made that fail with a bogus
// "Check internet connection". The URL lives until the portal tab closes — a few MB, worth it.
export const viewPortalPdf = async (id: string): Promise<void> => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/proposals/${id}/pdf`, { responseType: 'blob' });
  window.open(window.URL.createObjectURL(data), '_blank');
};

// item 865 — DOCUMENTS on a pricing request: the customer's supporting files (floor plans,
// equipment lists, photos). Server-side they land in the project-document store under the
// proposal's folder, so the reviewing team sees them on the internal proposal page + project
// tree instantly. Upload is open until we SEND the priced version (the server enforces it).
export interface PortalDocRow {
  id: string;
  name: string;
  contentType: string;
  length: number;
  createdDate: string;
  mine: boolean; // uploaded by this login → deletable pre-send
}

export const usePortalProposalDocs = (id?: string) => useQuery(
  ['portal-proposal-docs', id],
  async () => {
    const { data } = await axios.get(`${apiUrl()}customer-portal/proposals/${id}/documents`);
    return data as { files: PortalDocRow[]; canUpload: boolean };
  },
  { enabled: !!id },
);

export const uploadPortalDoc = async (proposalId: string, file: File): Promise<void> => {
  const dataBase64 = await toBase64(file);
  await axios.post(`${apiUrl()}customer-portal/proposals/${proposalId}/documents`, {
    name: file.name,
    contentType: file.type,
    dataBase64,
  });
};

export const downloadPortalDoc = async (proposalId: string, fileId: string, name: string): Promise<void> => {
  const res = await axios.get(`${apiUrl()}customer-portal/proposals/${proposalId}/documents/${fileId}/download`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const deletePortalDoc = async (proposalId: string, fileId: string): Promise<void> => {
  await axios.delete(`${apiUrl()}customer-portal/proposals/${proposalId}/documents/${fileId}`);
};

// item 862 — the venue-builder CATALOG, price-stripped server-side (system sizes, quantity
// catalog, equipment categories, sources — every rate zeroed before it leaves the backend).
export const usePortalVenueCatalog = () => useQuery(
  'portal-venue-catalog',
  async () => {
    const { data } = await axios.get(`${apiUrl()}customer-portal/venue-catalog`);
    return data as { cfgJson: string };
  },
  { staleTime: 300_000 },
);

// item 853 — the SCOPE-ONLY PDF (no pricing): available at any stage, unlike the priced PDF.
// item 867 — never revoked (see viewPortalPdf): the viewer's download re-fetches the blob URL.
export const viewPortalScopePdf = async (id: string): Promise<void> => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/proposals/${id}/scope-pdf`, { responseType: 'blob' });
  window.open(window.URL.createObjectURL(data), '_blank');
};

// item 871 — my customer's open projects (names + ids only), for the new-proposal chooser
// and the per-project "Add proposal" buttons.
export interface PortalProjectRow { id: string; name: string }

export const usePortalProjects = () => useQuery('portal-projects', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/projects`);
  return data as PortalProjectRow[];
});

// item 868c — DEALS: everything SENT-and-beyond for my customer, grouped by project. The
// customer can APPROVE a sent quote from here (the same soft client approval the public
// link records — Pulse still runs the binding approval wizard internally).
export interface PortalDealRow {
  id: string;
  proposalNumber: string;
  title: string;
  projectId: string;
  projectName?: string | null;
  status: string;
  sentDate?: string | null;
  clientApprovedDate?: string | null;
  clientApprovedByName?: string | null;
  canApprove: boolean;
  // item 1365 — customer-side decline (same window as approve)
  canDecline?: boolean;
  clientDeclinedDate?: string | null;
  clientDeclinedByName?: string | null;
  clientDeclineReason?: string | null;
  canViewPricedPdf: boolean;
  isCustomerSubmission: boolean;
  isRnwy: boolean;
  offersPerpetual: boolean;
  offersTwoYear: boolean;
  offersFiveYear: boolean;
  offersHelpDesk: boolean;
  // item 1476 — the deal's project lead(s): name + email only (In progress / Complete deals)
  projectLeads?: { name: string; email?: string | null }[] | null;
}

export const usePortalDeals = () => useQuery('portal-deals', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/deals`);
  return data as PortalDealRow[];
});

// item 1365 — mark a sent, still-pending quote as declined by the customer (moves it to Declined)
export const declinePortalDeal = async (id: string, declinedByName: string, reason: string): Promise<void> => {
  await axios.post(`${apiUrl()}customer-portal/proposals/${id}/decline`, { declinedByName, reason });
};

export const approvePortalDeal = async (
  id: string,
  approvedByName: string,
  rnwySaleType: number,
  rnwyHelpDeskTier: number,
): Promise<void> => {
  await axios.post(`${apiUrl()}customer-portal/proposals/${id}/approve`, {
    approvedByName, rnwySaleType, rnwyHelpDeskTier,
  });
};

// item 868d — finalized INVOICES (amounts + status), per-user opt-in (admin grants
// ShowPortalInvoices on the user form). enabled:false → the section never renders.
export interface PortalInvoiceRow {
  id: string;
  invoiceNumber: string;
  name?: string | null;
  projectName?: string | null;
  date: string;
  dueDate?: string | null;
  total: number;
  paid: boolean;
  pastDue?: boolean;
  // item 1594 — the customer's bank debit is clearing (item 1593): "Payment received", never past due
  clearing?: boolean;
  clearingSince?: string | null;
}

// item 1420 — an OPEN subscription charge (a Stripe subscription invoice not yet paid), listed
// with the invoices so Accounting's open list and Total Open match the Statement PDF
export interface PortalOpenChargeRow {
  id: string;
  number?: string | null;
  subscriptionName?: string | null;
  projectName?: string | null;
  date: string;
  dueDate: string;
  total: number;
  pastDue: boolean;
  clearing?: boolean; // item 1594
  clearingSince?: string | null;
}

// item 1486 — the customer's OPEN account credit (unapplied overpayment); applies to the next invoice
export interface PortalCreditRow {
  id: string;
  number: string;
  date: string;
  amount: number;
  remaining: number;
  note?: string | null;
  projectName?: string | null;
}

export const usePortalInvoices = () => useQuery('portal-invoices', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/invoices`);
  return data as { enabled: boolean; rows: PortalInvoiceRow[]; charges?: PortalOpenChargeRow[]; credits?: PortalCreditRow[] };
});

// ---- item 1402 — SALES ORDERS: frozen snapshots only (the server never reads a work order) ----

export interface PortalSalesOrderRow {
  id: string;
  number: string;
  title?: string | null;
  projectName?: string | null;
  date: string;
  total: number;
  status: string; // Awaiting your approval | Approved | Declined | In progress | Complete | Closed
  sentDate: string;
  approvedByName?: string | null;
  approvedDate?: string | null;
  declinedByName?: string | null;
  declinedDate?: string | null;
  canApprove: boolean;
  converted: boolean;
}

export const usePortalSalesOrders = () => useQuery('portal-sales-orders', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/sales-orders`);
  return data as PortalSalesOrderRow[];
});

// the customer's click IS the approval (first click wins; the admin bell rings server-side)
export const approvePortalSalesOrder = async (id: string, name: string): Promise<void> => {
  await axios.post(`${apiUrl()}customer-portal/sales-orders/${id}/approve`, { name });
};

export const declinePortalSalesOrder = async (id: string, name: string, reason?: string): Promise<void> => {
  await axios.post(`${apiUrl()}customer-portal/sales-orders/${id}/decline`, { name, reason });
};

// ---- item 891 — the BILLING half: subscriptions, self-serve auto-pay, invoice pay links ----

export interface PortalSubscriptionCharge {
  id: string; // item 1420
  number?: string | null;
  amount: number;
  issuedDate: string;
  paidDate?: string | null;
  clearingSince?: string | null; // item 1594
  status: string; // Issued | Overdue | Payment received | Paid (client-friendly — internal states never leave the server)
}

export interface PortalSubscriptionRow {
  id: string;
  name?: string | null;
  projectName?: string | null; // item 1157 — WHAT the subscription is for
  amount: number;
  frequency: number;
  status: string; // Active | Paused | Cancelled — display only, no controls here
  nextBillDate?: string | null;
  endDate?: string | null;
  autoPayEnrolled: boolean;
  canManageAutoPay: boolean;
  charges: PortalSubscriptionCharge[];
}

export const usePortalSubscriptions = (enabled: boolean) => useQuery('portal-subscriptions', async () => {
  const { data } = await axios.get(`${apiUrl()}customer-portal/subscriptions`);
  return data as PortalSubscriptionRow[];
}, { enabled });

// ENROLL: the server hands back the durable auto-pay page (item 882); completing it flips the
// subscription to charge-automatically AND charges any open invoices (item 888).
export const portalAutoPayLink = async (subscriptionId: string): Promise<string> => {
  const { data } = await axios.post(`${apiUrl()}customer-portal/subscriptions/${subscriptionId}/autopay-link`);
  return data.url as string;
};

export const portalAutoPayOff = async (subscriptionId: string): Promise<void> => {
  await axios.post(`${apiUrl()}customer-portal/subscriptions/${subscriptionId}/autopay/disable`);
};

// Pay a one-time invoice by ACH — mints the same Stripe pay link the emailed invoice carries.
// item 1420 — pay an open subscription charge: Stripe's hosted invoice page for that charge
export const portalChargePayLink = async (chargeId: string): Promise<string> => {
  const { data } = await axios.post(`${apiUrl()}customer-portal/subscription-charges/${chargeId}/pay-link`);
  return data.url as string;
};

export const portalInvoicePayLink = async (invoiceId: string): Promise<string> => {
  const { data } = await axios.post(`${apiUrl()}customer-portal/invoices/${invoiceId}/pay-link`);
  return data.url as string;
};

// item 852 — the portal's own brand (name/logo/colors/font): RNWY-kit defaults, admin-tunable
// in Settings → Proposal Portal. Cached locally so the portal boots straight into its look
// instead of flashing the tenant theme while the request is in flight.
export interface PortalBrand {
  portalName: string;
  tagline?: string; // item 1181 — the partnership line ("Home of RNWY™")
  logoDataUrl: string | null;
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
}

const PORTAL_BRAND_CACHE_KEY = 'basix-portal-brand-cache';

export const readPortalBrandCache = (): PortalBrand | undefined => {
  try {
    const raw = window.localStorage.getItem(PORTAL_BRAND_CACHE_KEY);
    return raw ? (JSON.parse(raw) as PortalBrand) : undefined;
  } catch {
    return undefined;
  }
};

export const usePortalBrand = () => useQuery(
  'portal-brand',
  async () => {
    const { data } = await axios.get(`${apiUrl()}customer-portal/brand`);
    return data as PortalBrand;
  },
  {
    initialData: readPortalBrandCache,
    // item 860 — cache is a paint hint only; always revalidate so admin brand edits land
    initialDataUpdatedAt: 0,
    onSuccess: (data) => {
      try {
        window.localStorage.setItem(PORTAL_BRAND_CACHE_KEY, JSON.stringify(data));
      } catch {
        // storage full/blocked — just skip the cache
      }
    },
  },
);
